import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Navigate, Link } from 'react-router-dom';
import UserActivityMonitor from '../components/UserActivityMonitor';

interface Statistics {
  overview: {
    totalPhotos: number;
    totalComments: number;
    totalLikes: number;
    totalAudioClips: number;
    totalTags: number;
    totalUsers: number;
    activeUsers: number;
  };
  engagement: {
    totalInteractions: number;
    avgLikesPerPhoto: number;
    avgCommentsPerPhoto: number;
    avgAudioClipsPerPhoto: number;
    photosWithComments: number;
    photosWithLikes: number;
    photosWithAudioClips: number;
  };
  topPhotos: Array<{
    photoId: string;
    fileName: string;
    imageUrl: string;
    thumbnailUrls: {
      small: string;
      medium: string;
      large: string;
    };
    ownerName: string;
    likeCount: number;
    commentCount: number;
    audioClipCount: number;
    totalEngagement: number;
  }>;
  topContributors: Array<{
    userId: string;
    name: string;
    email: string;
    photoCount: number;
    commentCount: number;
    likeCount: number;
    audioClipCount: number;
    totalActivity: number;
  }>;
  activityByMonth: Array<{
    month: string;
    photoCount: number;
    commentCount: number;
    likeCount: number;
    audioClipCount: number;
  }>;
  tagUsage: {
    totalUniqueTags: number;
    byType: {
      person: number;
      place: number;
      thing: number;
    };
    topTags: Array<{
      tag: string;
      type: string;
      count: number;
    }>;
  };
}

export default function StatisticsPage() {
  const { isSuperAdmin } = useAuthStore();

  // Only superadmins can access this page
  if (!isSuperAdmin) {
    return <Navigate to="/feed" replace />;
  }

  const { data: stats, isLoading, error } = useQuery<Statistics>({
    queryKey: ['statistics'],
    queryFn: () => api.getStatistics(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold text-red-900 mb-2">Error Loading Statistics</h2>
            <p className="text-red-700">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const engagementRate = stats.overview.totalPhotos > 0
    ? ((stats.engagement.photosWithLikes + stats.engagement.photosWithComments) / stats.overview.totalPhotos * 100).toFixed(1)
    : '0.0';

  const userParticipationRate = stats.overview.totalUsers > 0
    ? (stats.overview.activeUsers / stats.overview.totalUsers * 100).toFixed(1)
    : '0.0';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">📊</span>
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                Site Statistics
              </h1>
              <p className="text-gray-600 mt-1">Engagement metrics and usage insights</p>
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard icon="📸" label="Photos" value={stats.overview.totalPhotos} color="from-blue-500 to-blue-600" />
          <StatCard icon="❤️" label="Likes" value={stats.overview.totalLikes} color="from-pink-500 to-pink-600" />
          <StatCard icon="💬" label="Comments" value={stats.overview.totalComments} color="from-purple-500 to-purple-600" />
          <StatCard icon="🏷️" label="Tags" value={stats.overview.totalTags} color="from-green-500 to-green-600" />
          <StatCard icon="👥" label="Users" value={stats.overview.totalUsers} color="from-orange-500 to-orange-600" />
          <StatCard icon="✨" label="Active Users" value={stats.overview.activeUsers} color="from-indigo-500 to-indigo-600" />
        </div>

        {/* Engagement Metrics */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>💡</span>
              Engagement Insights
            </h2>
            <div className="space-y-4">
              <MetricRow label="Total Interactions" value={stats.engagement.totalInteractions.toLocaleString()} />
              <MetricRow label="Photos with Engagement" value={`${engagementRate}%`} 
                subtext={`${stats.engagement.photosWithLikes + stats.engagement.photosWithComments} of ${stats.overview.totalPhotos} photos`} />
              <MetricRow label="Avg Likes per Photo" value={stats.engagement.avgLikesPerPhoto.toFixed(2)} />
              <MetricRow label="Avg Comments per Photo" value={stats.engagement.avgCommentsPerPhoto.toFixed(2)} />
              <MetricRow label="User Participation" value={`${userParticipationRate}%`}
                subtext={`${stats.overview.activeUsers} of ${stats.overview.totalUsers} users have interacted with photos`} />
            </div>
          </div>

          {/* Tag Usage */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>🏷️</span>
              Tag Usage
            </h2>
            <div className="space-y-4">
              <MetricRow label="Unique Tags" value={stats.tagUsage.totalUniqueTags.toLocaleString()} />
              <MetricRow label="People Tags" value={stats.tagUsage.byType.person.toLocaleString()} icon="👤" />
              <MetricRow label="Place Tags" value={stats.tagUsage.byType.place.toLocaleString()} icon="📍" />
              <MetricRow label="Thing Tags" value={stats.tagUsage.byType.thing.toLocaleString()} icon="🎯" />
            </div>
          </div>
        </div>

        {/* Top Photos */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏆</span>
            Top Engaged Photos
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Photo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Owner</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Likes</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Comments</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Audio</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {stats.topPhotos.slice(0, 10).map((photo, idx) => (
                  <tr key={photo.photoId} className="border-b border-gray-100 hover:bg-primary-50 transition-colors">
                    <td className="py-3 px-4">
                      <Link to={`/feed?photoId=${photo.photoId}`} className="flex items-center gap-3 group">
                        <span className="text-gray-400 text-sm w-6">#{idx + 1}</span>
                        <img 
                          src={photo.thumbnailUrls.small} 
                          alt={photo.fileName}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                        <span className="text-sm text-gray-900 group-hover:text-primary-600 truncate max-w-xs font-medium hover:underline">{photo.fileName}</span>
                      </Link>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{photo.ownerName}</td>
                    <td className="py-3 px-4 text-center text-sm">
                      <span className="text-pink-600 font-medium">{photo.likeCount}</span>
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      <span className="text-purple-600 font-medium">{photo.commentCount}</span>
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      <span className="text-green-600 font-medium">{photo.audioClipCount}</span>
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      <span className="font-bold text-gray-900">{photo.totalEngagement}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Contributors */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🌟</span>
            Top Contributors
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">User</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Photos</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Comments</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Likes</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Audio Clips</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Total Activity</th>
                </tr>
              </thead>
              <tbody>
                {stats.topContributors.map((user, idx) => (
                  <tr key={user.userId} className="border-b border-gray-100 hover:bg-primary-50 transition-colors cursor-pointer">
                    <td className="py-3 px-4">
                      <Link to={`/statistics/user/${user.userId}`} className="flex items-center gap-2 group">
                        <span className="text-gray-400 text-sm w-6">#{idx + 1}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900 group-hover:text-primary-600">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {user.photoCount > 0 ? (
                        <Link to={`/statistics/user/${user.userId}`} className="text-blue-600 hover:text-blue-700 font-medium hover:underline">
                          {user.photoCount}
                        </Link>
                      ) : (
                        <span className="text-gray-400">{user.photoCount}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {user.commentCount > 0 ? (
                        <Link to={`/statistics/user/${user.userId}`} className="text-purple-600 hover:text-purple-700 font-medium hover:underline">
                          {user.commentCount}
                        </Link>
                      ) : (
                        <span className="text-gray-400">{user.commentCount}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {user.likeCount > 0 ? (
                        <Link to={`/statistics/user/${user.userId}`} className="text-pink-600 hover:text-pink-700 font-medium hover:underline">
                          {user.likeCount}
                        </Link>
                      ) : (
                        <span className="text-gray-400">{user.likeCount}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {user.audioClipCount > 0 ? (
                        <Link to={`/statistics/user/${user.userId}`} className="text-green-600 hover:text-green-700 font-medium hover:underline">
                          {user.audioClipCount}
                        </Link>
                      ) : (
                        <span className="text-gray-400">{user.audioClipCount}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {user.totalActivity > 0 ? (
                        <Link to={`/statistics/user/${user.userId}`} className="font-bold text-gray-900 hover:text-primary-600 hover:underline">
                          {user.totalActivity}
                        </Link>
                      ) : (
                        <span className="text-gray-400">{user.totalActivity}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>📈</span>
            Activity by Month
          </h2>
          <div className="space-y-3">
            {stats.activityByMonth.slice(-12).reverse().map((month) => {
              const total = month.photoCount + month.commentCount + month.likeCount;
              const maxActivity = Math.max(...stats.activityByMonth.map(m => m.photoCount + m.commentCount + m.likeCount));
              const barWidth = maxActivity > 0 ? (total / maxActivity * 100) : 0;
              
              return (
                <div key={month.month} className="flex items-center gap-4">
                  <div className="w-24 text-sm font-medium text-gray-700">{month.month}</div>
                  <div className="flex-1">
                    <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                      <div 
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500"
                        style={{ width: `${barWidth}%` }}
                      ></div>
                      <div className="absolute inset-0 flex items-center px-3 text-xs font-medium text-gray-700">
                        <span className="bg-white/90 px-2 py-0.5 rounded">
                          {month.photoCount} photos • {month.commentCount} comments • {month.likeCount} likes
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="w-16 text-right text-sm font-bold text-gray-900">{total}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Tags */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🔥</span>
            Most Used Tags
          </h2>
          <div className="flex flex-wrap gap-2">
            {stats.tagUsage.topTags.slice(0, 30).map((tag) => {
              const typeColors = {
                person: 'bg-blue-100 text-blue-800 border-blue-200',
                place: 'bg-green-100 text-green-800 border-green-200',
                thing: 'bg-purple-100 text-purple-800 border-purple-200',
              };
              const typeEmojis = {
                person: '👤',
                place: '📍',
                thing: '🎯',
              };
              
              return (
                <div
                  key={`${tag.tag}-${tag.type}`}
                  className={`px-3 py-2 rounded-lg border-2 ${typeColors[tag.type as keyof typeof typeColors] || 'bg-gray-100 text-gray-800 border-gray-200'} font-medium text-sm flex items-center gap-2`}
                >
                  <span>{typeEmojis[tag.type as keyof typeof typeEmojis] || '🏷️'}</span>
                  <span>{tag.tag}</span>
                  <span className="opacity-60">({tag.count})</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* User Activity Monitor */}
        <UserActivityMonitor />

        {/* Health Score */}
        <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl shadow-xl p-6 border-2 border-primary-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🎯</span>
            Platform Health Assessment
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <HealthIndicator
              label="User Engagement"
              score={parseFloat(engagementRate)}
              threshold={30}
              description="Percentage of photos with likes or comments"
            />
            <HealthIndicator
              label="User Participation"
              score={parseFloat(userParticipationRate)}
              threshold={50}
              description="Percentage of users who have interacted with at least one photo (uploaded, liked, commented, or added audio)"
            />
            <HealthIndicator
              label="Interaction Rate"
              score={stats.overview.totalPhotos > 0 ? (stats.engagement.totalInteractions / stats.overview.totalPhotos) : 0}
              threshold={2}
              description="Average interactions per photo"
            />
          </div>
          
          {parseFloat(engagementRate) < 20 && parseFloat(userParticipationRate) < 30 && (
            <div className="mt-6 p-4 bg-yellow-100 border-2 border-yellow-300 rounded-lg">
              <p className="text-yellow-900 font-medium">
                ⚠️ Low engagement detected. Consider encouraging users to interact more with photos or evaluating if the platform meets family needs.
              </p>
            </div>
          )}
          
          {parseFloat(engagementRate) >= 50 && parseFloat(userParticipationRate) >= 70 && (
            <div className="mt-6 p-4 bg-green-100 border-2 border-green-300 rounded-lg">
              <p className="text-green-900 font-medium">
                ✅ Excellent engagement! The family is actively using and enjoying the platform.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper Components
function StatCard({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-4 hover:shadow-xl transition-shadow">
      <div className={`w-10 h-10 bg-gradient-to-br ${color} rounded-lg flex items-center justify-center mb-2 text-xl`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</div>
      <div className="text-sm text-gray-600 mt-1">{label}</div>
    </div>
  );
}

function MetricRow({ label, value, subtext, icon }: { label: string; value: string; subtext?: string; icon?: string }) {
  return (
    <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2">
        {icon && <span>{icon}</span>}
        <div>
          <div className="text-sm text-gray-600">{label}</div>
          {subtext && <div className="text-xs text-gray-400 mt-0.5">{subtext}</div>}
        </div>
      </div>
      <div className="text-lg font-bold text-gray-900">{value}</div>
    </div>
  );
}

function HealthIndicator({ label, score, threshold, description }: { label: string; score: number; threshold: number; description: string }) {
  const isHealthy = score >= threshold;
  const percentage = Math.min(100, (score / threshold) * 100);
  
  return (
    <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-gray-700">{label}</div>
        <div className={`text-2xl ${isHealthy ? 'text-green-600' : 'text-yellow-600'} font-bold`}>
          {score.toFixed(1)}{typeof score === 'number' && score < 10 ? '' : '%'}
        </div>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full transition-all duration-500 ${isHealthy ? 'bg-green-500' : 'bg-yellow-500'}`}
          style={{ width: `${Math.min(100, percentage)}%` }}
        ></div>
      </div>
      <div className="text-xs text-gray-500">{description}</div>
    </div>
  );
}

